import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { VenueReview } from '@guabi/shared';
import { Button } from '@/components/ui/Button';
import { Field, SelectInput, TextInput } from '@/components/ui/Field';
import { useDeleteMyReview, useUpsertMyReview } from './queries';
import { StarPicker } from './Stars';

/**
 * The caller's own review. The server keys it by their user id, so saving twice
 * replaces rather than duplicates, and nobody can edit anyone else's opinion.
 */
export function ReviewForm({
  wedId,
  venueId,
  mine,
}: {
  wedId: string;
  venueId: string;
  mine?: VenueReview;
}) {
  const { t } = useTranslation();
  const upsert = useUpsertMyReview(wedId);
  const remove = useDeleteMyReview(wedId);

  const [stars, setStars] = useState(mine?.stars ?? 4);
  const [liked, setLiked] = useState(mine?.liked ?? '');
  const [disliked, setDisliked] = useState(mine?.disliked ?? '');
  const [dealbreakers, setDealbreakers] = useState(mine?.dealbreakers ?? '');
  const [wouldBook, setWouldBook] = useState(
    mine?.wouldBook === undefined ? '' : mine.wouldBook ? 'yes' : 'no',
  );
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(undefined);
    setSaved(false);
    try {
      await upsert.mutateAsync({
        venueId,
        input: {
          stars,
          liked,
          disliked,
          dealbreakers,
          ...(wouldBook ? { wouldBook: wouldBook === 'yes' } : {}),
        },
      });
      setSaved(true);
    } catch {
      setError(t('common.somethingWrong'));
    }
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-3 rounded border border-line p-3">
      <h4 className="text-caption font-medium text-ink">
        {mine ? t('venues.yourReviewEdit') : t('venues.yourReview')}
      </h4>

      <StarPicker
        name={`stars-${venueId}`}
        label={t('venues.stars')}
        value={stars}
        onChange={setStars}
      />

      <Field label={t('venues.liked')}>
        {({ id }) => (
          <TextInput
            id={id}
            maxLength={5000}
            value={liked}
            onChange={(e) => setLiked(e.target.value)}
          />
        )}
      </Field>

      <Field label={t('venues.disliked')}>
        {({ id }) => (
          <TextInput
            id={id}
            maxLength={5000}
            value={disliked}
            onChange={(e) => setDisliked(e.target.value)}
          />
        )}
      </Field>

      <Field label={t('venues.dealbreakers')} hint={t('venues.dealbreakersHint')}>
        {({ id, describedBy }) => (
          <TextInput
            id={id}
            aria-describedby={describedBy}
            maxLength={5000}
            value={dealbreakers}
            onChange={(e) => setDealbreakers(e.target.value)}
          />
        )}
      </Field>

      <Field label={t('venues.wouldBookQuestion')}>
        {({ id }) => (
          <SelectInput id={id} value={wouldBook} onChange={(e) => setWouldBook(e.target.value)}>
            <option value="">{t('venues.undecided')}</option>
            <option value="yes">{t('venues.wouldBook')}</option>
            <option value="no">{t('venues.wouldNotBook')}</option>
          </SelectInput>
        )}
      </Field>

      {error && (
        <p role="alert" className="text-caption text-status-declinedFg">
          {error}
        </p>
      )}
      {saved && (
        <p role="status" className="text-caption text-leaf-soft">
          {t('venues.reviewSaved')}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Button type="submit" loading={upsert.isPending}>
          {mine ? t('venues.updateReview') : t('venues.saveReview')}
        </Button>
        {mine && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => void remove.mutateAsync({ venueId })}
          >
            {t('venues.removeReview')}
          </Button>
        )}
      </div>
    </form>
  );
}
